import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  List,
  ListItem,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  CalendarToday,
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
  CloudUpload,
  CheckCircle,
  Map as MapIcon,
  Navigation as NavigationIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import {
  bucketJobsForDay,
  countJobsByDayInMonth,
  formatJobTime,
  googleMapsDirectionsUrl,
  jobMatchesDay,
  sortTodaysStops,
} from '../utils/jobSchedule';
import {
  computeDayTurnover,
  formatDriverMoney,
  parseJobPriceAmount,
} from '../utils/driverPortalDayTotals';
import DriverPortalJobDetailContent from './DriverPortalJobDetailContent';
import { phoneTelHref, resolveJobPhone } from '../utils/driverPortalJobContact';

dayjs.locale('en');

const WEEKDAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Formatted price for driver portal (GBP default). Returns null if no numeric price. */
function formatDriverJobPrice(job) {
  const n = parseJobPriceAmount(job);
  if (n === null) return null;
  const cur = String(job.currency || 'GBP').trim() || 'GBP';
  return formatDriverMoney(n, cur);
}

function generateMonthCells(monthDayjs) {
  const startOfMonth = monthDayjs.startOf('month');
  const endOfMonth = monthDayjs.endOf('month');
  const startDate = startOfMonth.startOf('week');
  const endDate = endOfMonth.endOf('week');
  const days = [];
  let current = startDate;
  while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
    days.push(current);
    current = current.add(1, 'day');
  }
  return days;
}

export default function DriverPortalSchedule({
  jobs,
  selectedDay: selectedDayProp = null,
  onSelectedDayChange,
  onFileUpload,
  onStatusChange,
  onCompleteJob,
}) {
  const [monthCursor, setMonthCursor] = useState(() => dayjs());
  const [selectedDayInternal, setSelectedDayInternal] = useState(null);
  const selectedDay = onSelectedDayChange ? selectedDayProp : selectedDayInternal;
  const setSelectedDay = onSelectedDayChange || setSelectedDayInternal;
  const [detailJob, setDetailJob] = useState(null);

  useEffect(() => {
    if (selectedDay?.isValid?.()) {
      setMonthCursor(selectedDay.startOf('month'));
    }
  }, [selectedDay]);

  const countsByDay = useMemo(
    () => countJobsByDayInMonth(jobs, monthCursor),
    [jobs, monthCursor]
  );

  const calendarDays = useMemo(
    () => generateMonthCells(monthCursor),
    [monthCursor]
  );

  const dayBuckets = useMemo(() => {
    if (!selectedDay) return null;
    return bucketJobsForDay(jobs, selectedDay);
  }, [jobs, selectedDay]);

  const todaysStops = useMemo(() => sortTodaysStops(jobs, dayjs()), [jobs]);
  const isViewingToday = selectedDay && selectedDay.isSame(dayjs(), 'day');

  const dayTurnover = useMemo(() => {
    if (!selectedDay) return null;
    return computeDayTurnover(jobs, selectedDay);
  }, [jobs, selectedDay]);

  const openDetail = (job) => setDetailJob(job);
  const closeDetail = () => setDetailJob(null);

  const resolvedDetail = detailJob
    ? jobs.find((j) => j.id === detailJob.id) || detailJob
    : null;

  const renderTodaysRouteSection = () => {
    const mapUrl = (job) => googleMapsDirectionsUrl(job.address);
    const st = (job) => String(job.status || 'pending').toLowerCase();

    return (
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderColor: 'primary.main',
          borderWidth: 1,
          bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(79, 70, 229, 0.08)' : 'rgba(79, 70, 229, 0.04)'),
        }}
      >
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <NavigationIcon color="primary" fontSize="small" />
            Today&apos;s stops
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {dayjs().format('dddd, D MMMM YYYY')} · Tap a row for full details. Order: in progress, then by time.
          </Typography>
          {todaysStops.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No tasks scheduled for today. Open the calendar to pick another day.
            </Typography>
          ) : (
            <List dense disablePadding>
              {todaysStops.map((job) => {
                const priceLabel = formatDriverJobPrice(job);
                const phone = resolveJobPhone(job);
                const tel = phoneTelHref(phone);
                return (
                <ListItem
                  key={job.id}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    py: 1.25,
                    px: 0,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => openDetail(job)}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {formatJobTime(job) ? `${formatJobTime(job)} · ` : ''}
                        {job.name || job.client || 'Job'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {job.client || job.client_name || '—'}
                        {tel ? (
                          <>
                            {' · '}
                            <Link
                              href={tel}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ fontWeight: 700, color: 'primary.main' }}
                            >
                              {phone}
                            </Link>
                          </>
                        ) : null}
                        {job.address ? ` · ${job.address}` : ''}
                        {priceLabel ? ` · ${priceLabel}` : ''}
                      </Typography>
                    </Box>
                    <Chip
                      label={job.status || 'pending'}
                      size="small"
                      sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                      color={st(job) === 'completed' ? 'success' : st(job) === 'in_progress' ? 'primary' : 'default'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {job.address && mapUrl(job) ? (
                      <Button
                        component="a"
                        href={mapUrl(job)}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="outlined"
                        startIcon={<MapIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        Maps
                      </Button>
                    ) : null}
                    <Button size="small" variant="text" onClick={() => openDetail(job)} sx={{ textTransform: 'none' }}>
                      Details
                    </Button>
                    {st(job) === 'pending' ? (
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => onStatusChange(job.id, 'in_progress')}
                        sx={{ textTransform: 'none' }}
                      >
                        Arrived
                      </Button>
                    ) : null}
                  </Box>
                </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderDayTurnoverFooter = () => {
    if (!selectedDay || !dayTurnover) return null;

    const { jobCount, completedJobCount, currencies, hasPricedJobs, jobsWithoutPrice } = dayTurnover;

    if (jobCount === 0) {
      return (
        <Card variant="outlined" sx={{ mt: 3, bgcolor: 'action.hover' }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
              Day turnover
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No tasks scheduled for this day.
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card
        variant="outlined"
        sx={{
          mt: 3,
          borderColor: 'success.main',
          borderWidth: 1,
          bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.08)' : 'rgba(34, 197, 94, 0.05)'),
        }}
      >
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
            Day turnover
          </Typography>

          {!hasPricedJobs ? (
            <Typography variant="body2" color="text.secondary">
              {jobCount} task{jobCount === 1 ? '' : 's'} — no prices set on jobs for this day.
            </Typography>
          ) : (
            currencies.map((row) => (
              <Box key={row.currency} sx={{ mb: currencies.length > 1 ? 2 : 0 }}>
                {currencies.length > 1 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                    {row.currency}
                  </Typography>
                ) : null}
                <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.5 }}>
                  Total cost completed:{' '}
                  <Box component="span" sx={{ color: 'success.main' }}>
                    {formatDriverMoney(row.completed, row.currency)}
                  </Box>
                  {' / '}
                  Total cost: {formatDriverMoney(row.total, row.currency)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 600, color: 'text.secondary' }}>
                  Approximately remaining: {formatDriverMoney(row.remaining, row.currency)}
                </Typography>
              </Box>
            ))
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {completedJobCount} of {jobCount} task{jobCount === 1 ? '' : 's'} completed
            {jobsWithoutPrice > 0
              ? ` · ${jobsWithoutPrice} without price (not included in totals)`
              : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Based on job sell price. Cancelled tasks are excluded.
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const renderJobSlab = (job) => {
    const priceLabel = formatDriverJobPrice(job);
    return (
    <Button
      key={job.id}
      variant="outlined"
      onClick={() => openDetail(job)}
      sx={{
        justifyContent: 'flex-start',
        textAlign: 'left',
        py: 1,
        px: 1.5,
        borderRadius: 2,
        borderColor: 'divider',
        display: 'block',
        width: '100%',
        mb: 0.75,
        textTransform: 'none',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
        {formatJobTime(job) ? `${formatJobTime(job)} · ` : ''}
        {job.name || job.client || 'Job'}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {job.client || job.client_name || '—'}
        {job.address ? ` · ${job.address}` : ''}
        {priceLabel ? ` · ${priceLabel}` : ''}
      </Typography>
      <Chip
        label={job.status || 'pending'}
        size="small"
        sx={{ mt: 0.5, height: 22, fontSize: '0.7rem' }}
        color={job.status === 'completed' ? 'success' : 'default'}
      />
    </Button>
    );
  };

  const renderJobDialog = () => {
    if (!resolvedDetail) return null;
    const job = resolvedDetail;

    return (
      <Dialog
        open
        onClose={closeDetail}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
          {job.name || 'Task'}
        </DialogTitle>
        <DialogContent dividers>
          <DriverPortalJobDetailContent job={job} selectedDay={selectedDay} />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Update task
          </Typography>
          <Box sx={{ mb: 2 }}>
            <input
              type="file"
              multiple
              id={`portal-file-${job.id}`}
              style={{ display: 'none' }}
              onChange={(e) => {
                onFileUpload(job.id, e.target.files);
                e.target.value = '';
              }}
            />
            <label htmlFor={`portal-file-${job.id}`}>
              <Button variant="outlined" component="span" startIcon={<CloudUpload />} size="small">
                Upload files
              </Button>
            </label>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
              mb: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={job.status || 'pending'}
                label="Status"
                onChange={(e) => onStatusChange(job.id, e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            {String(job.status || '').toLowerCase() === 'pending' && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={() => onStatusChange(job.id, 'in_progress')}
                sx={{ textTransform: 'none' }}
              >
                Arrived
              </Button>
            )}
            {String(job.status || '').toLowerCase() !== 'completed' &&
              String(job.status || '').toLowerCase() !== 'cancelled' && (
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircle />}
                onClick={() => onCompleteJob(job.id)}
                sx={{ textTransform: 'none' }}
              >
                Complete
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (selectedDay) {
    return (
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          width: '100%',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {selectedDay.format('dddd, D MMMM YYYY')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Hourly schedule (local time). Tap a task for details, status, and files.
        </Typography>

        {isViewingToday ? renderTodaysRouteSection() : null}

        {dayBuckets && dayBuckets.unscheduled.length > 0 && (
          <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.hover' }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                No time assigned
              </Typography>
              {dayBuckets.unscheduled.map((job) => renderJobSlab(job))}
            </CardContent>
          </Card>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {Array.from({ length: 24 }, (_, h) => h).map((hour) => {
            const list = dayBuckets?.byHour[hour] || [];
            return (
              <Box
                key={hour}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '72px 1fr', sm: '88px 1fr' },
                  gap: { xs: 1, sm: 2 },
                  py: 1.25,
                  minHeight: list.length ? 'auto' : 48,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  alignItems: 'start',
                  bgcolor: list.length ? 'transparent' : 'action.hover',
                }}
              >
                <Box sx={{ pt: 0.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      color: 'primary.main',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {String(hour).padStart(2, '0')}:00
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    – {String(hour).padStart(2, '0')}:59
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  {list.length > 0 ? (
                    list.map((job) => renderJobSlab(job))
                  ) : (
                    <Typography variant="body2" color="text.disabled" sx={{ py: 0.5 }}>
                      No tasks
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {dayBuckets &&
          dayBuckets.unscheduled.length === 0 &&
          jobs.filter((j) => jobMatchesDay(j, selectedDay)).length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No tasks for this day.
            </Typography>
          )}

        {renderDayTurnoverFooter()}

        {renderJobDialog()}
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, width: '100%' }}>
      {renderTodaysRouteSection()}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarToday color="primary" />
          Calendar
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={() => setMonthCursor((m) => m.subtract(1, 'month'))}>
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ minWidth: 160, textAlign: 'center', fontWeight: 600 }}>
            {monthCursor.format('MMMM YYYY')}
          </Typography>
          <IconButton onClick={() => setMonthCursor((m) => m.add(1, 'month'))}>
            <ChevronRight />
          </IconButton>
          <Button
            size="small"
            startIcon={<TodayIcon />}
            onClick={() => {
              const t = dayjs();
              setMonthCursor(t);
              setSelectedDay(t);
            }}
          >
            Today
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a day to see tasks by hour.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 0.5,
          mb: 0.5,
        }}
      >
        {WEEKDAYS_EN.map((d) => (
          <Typography
            key={d}
            align="center"
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', py: 0.5 }}
          >
            {d}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 0.5,
        }}
      >
        {calendarDays.map((d) => {
          const key = d.format('YYYY-MM-DD');
          const count = countsByDay[key] || 0;
          const inMonth = d.month() === monthCursor.month();
          const isToday = d.isSame(dayjs(), 'day');

          return (
            <Button
              key={key}
              fullWidth
              onClick={() => setSelectedDay(d)}
              sx={{
                minHeight: 56,
                p: 0.5,
                flexDirection: 'column',
                borderRadius: 1,
                color: inMonth ? 'text.primary' : 'text.disabled',
                border: isToday ? '2px solid' : '1px solid transparent',
                borderColor: isToday ? 'primary.main' : 'transparent',
                bgcolor: isToday ? 'action.selected' : 'transparent',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: isToday ? 800 : 600 }}>
                {d.date()}
              </Typography>
              {count > 0 && (
                <Chip
                  label={count}
                  size="small"
                  color="primary"
                  sx={{ height: 20, minWidth: 24, fontSize: '0.7rem', mt: 0.25 }}
                />
              )}
            </Button>
          );
        })}
      </Box>

      {jobs.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          No assigned tasks.
        </Typography>
      )}

      {renderJobDialog()}
    </Paper>
  );
}
